import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Phone, Mail } from "lucide-react";
import { adminApi } from "@/lib/api";
import { ChatThread } from "./ChatThread";
import { QuickReplies } from "./QuickReplies";
import { PushToggle } from "./PushToggle";
import { BroadcastButton } from "./Broadcast";

const ThreadList = ({ threads, users, active, onPick }) => {
  const [q, setQ] = useState("");
  const withChat = new Set(threads.map((t) => t.user_id));
  const others = users.filter((u) => u.role !== "super_admin" && !withChat.has(u.user_id));
  const match = (s) => (s || "").toLowerCase().includes(q.toLowerCase());
  const item = (id, name, email, sub, unread) => (
    <button key={id} data-testid={`chat-thread-${id}`} onClick={() => onPick(id)}
      className={`w-full text-left px-4 py-3 border-b border-[#d5dbde] hover:bg-[#f4f6f7] ${active === id ? "bg-[#f4f6f7] border-l-2 border-l-[#E0262C]" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate">{name || email}</span>
        {unread > 0 && <span data-testid={`chat-unread-${id}`} className="min-w-[18px] h-[18px] px-1 aurora-gradient text-white text-[10px] font-bold flex items-center justify-center rounded-full">{unread}</span>}
      </div>
      <div className="text-xs text-[#6b7280] truncate font-mono">{sub}</div>
    </button>
  );
  return (
    <div className="border-r border-[#d5dbde] flex flex-col h-[520px]">
      <div className="p-3 border-b border-[#d5dbde] relative">
        <Search className="w-3.5 h-3.5 absolute left-6 top-1/2 -translate-y-1/2 text-[#6b7280]" />
        <input data-testid="chat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…"
          className="w-full h-9 bg-[#f4f6f7] border border-[#d5dbde] pl-8 pr-2 text-sm text-[#2B3036] outline-none focus:border-[#E0262C]" />
      </div>
      <div className="flex-1 overflow-y-auto">
        {threads.filter((t) => match(t.name) || match(t.email)).map((t) => item(t.user_id, t.name, t.email, `${t.last.sender === "admin" ? "Tu: " : ""}${t.last.text}`, t.unread))}
        {q && others.filter((u) => match(u.name) || match(u.email)).map((u) => item(u.user_id, u.name, u.email, "Nuova conversazione", 0))}
        {!threads.length && !q && <div className="p-6 text-xs font-mono text-[#6b7280]">Nessuna conversazione. Cerca un cliente per scrivergli.</div>}
      </div>
    </div>
  );
};

const Conversation = ({ userId, onSent }) => {
  const [data, setData] = useState({ user: null, messages: [], typing: false });
  const load = useCallback(() => adminApi.chatThread(userId).then(setData).catch(() => {}), [userId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [load]);
  const send = async (text) => {
    try { const m = await adminApi.chatSend(userId, text); setData((d) => ({ ...d, messages: [...d.messages, m] })); onSent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Messaggio non inviato"); throw e; }
  };
  const u = data.user;
  return (
    <div className="flex flex-col">
      <div className="px-4 py-3 border-b border-[#d5dbde] h-[61px] flex flex-col justify-center">
        <div data-testid="chat-active-name" className="font-medium">{u?.name || "…"}</div>
        {u && <div className="text-xs font-mono text-[#6b7280] flex gap-4"><span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span>
          {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 hover:text-[#E0262C]"><Phone className="w-3 h-3" />{u.phone}</a>}</div>}
      </div>
      <ChatThread messages={data.messages} me="admin" onSend={send} placeholder="Scrivi al cliente… (Invio per inviare)" testid="admin-chat"
        typing={data.typing} typingLabel={u?.name?.split(" ")[0] || "Il cliente"} onTyping={() => adminApi.chatTyping(userId).catch(() => {})}
        renderTools={(setText) => <QuickReplies user={u} onPick={setText} />} />
    </div>
  );
};

export const AdminChatPanel = ({ initialUser }) => {
  const [threads, setThreads] = useState([]);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(initialUser || null);
  const load = useCallback(() => adminApi.chatThreads().then((d) => setThreads(d.threads)).catch(() => {}), []);
  useEffect(() => {
    load();
    adminApi.users().then((d) => setUsers(d.users)).catch(() => {});
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (initialUser) setActive(initialUser); }, [initialUser]);
  return (
    <div className="space-y-3">
    <div className="flex justify-between gap-2 flex-wrap"><BroadcastButton users={users} onSent={load} /><PushToggle /></div>
    <div data-testid="admin-chat-panel" className="border border-[#d5dbde] grid grid-cols-1 md:grid-cols-[320px_1fr]">
      <ThreadList threads={threads} users={users} active={active} onPick={setActive} />
      {active ? <Conversation key={active} userId={active} onSent={load} />
        : <div className="flex items-center justify-center text-sm text-[#6b7280] font-mono h-[520px]">Seleziona una conversazione</div>}
    </div>
    </div>
  );
};
